import React, { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { FormError } from '@/app/components/ui/FormError'
import { DiscreteLoader } from '@/app/components/ui/discrete-loader'
import { toast } from 'sonner'
import z from 'zod'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  TransferListItem,
  TransferProductItem,
  TransferRequest,
  transferService,
} from '@/app/services/transfer.service'

// ─── Validación (Zod) ────────────────────────────────────────────────────────
const formSchema = z.object({
  items: z.array(
    z.object({
      product_id: z.number(),
      product_name: z.string(),
      barcode: z.string().optional(),
      requested_quantity: z.number(),
      response_type: z.enum(['FULL', 'PARTIAL', 'REJECTED']),
      approved_quantity: z.number().min(0, 'Debe ser mayor o igual a 0'),
      response_reason: z.string().optional()
    })
  )
}).superRefine((data, ctx) => {
  data.items.forEach((item, index) => {
    if (item.response_type === 'PARTIAL') {
      if (item.approved_quantity <= 0 || item.approved_quantity >= item.requested_quantity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La cantidad parcial debe ser mayor a 0 y menor a lo solicitado.',
          path: ['items', index, 'approved_quantity'],
        })
      }

      if (!item.response_reason?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'El motivo es obligatorio para envios parciales.',
          path: ['items', index, 'response_reason'],
        })
      }
    }

    if (item.response_type === 'REJECTED' && !item.response_reason?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El motivo es obligatorio cuando no se envia el producto.',
        path: ['items', index, 'response_reason'],
      })
    }
  })
})

type FormValidate = z.infer<typeof formSchema>

interface SendReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedTransfer: TransferListItem | null
  onConfirm: (transfer: TransferListItem | null, status: Record<string, never>) => void
}

export function SendReviewModal({
  open,
  onOpenChange,
  selectedTransfer,
  onConfirm,
}: SendReviewModalProps) {
  const realTransfer = selectedTransfer?._originalData as TransferRequest | undefined
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValidate>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      items: []
    }
  })

  const { fields } = useFieldArray({
    control,
    name: 'items'
  })

  const watchItems = watch('items')

  useEffect(() => {
    if (open && realTransfer) {
      const initialItems = realTransfer.items.map((item: TransferProductItem) => ({
        product_id: item.product_id,
        product_name: item.product?.name || `Producto #${item.product_id}`,
        barcode: item.product?.barcode || '',
        requested_quantity: item.requested_quantity || 0,
        response_type: 'FULL' as const,
        approved_quantity: item.requested_quantity || 0,
        response_reason: ''
      }))
      reset({ items: initialItems })
    }
  }, [open, realTransfer, reset])

  const onSubmit = async (formData: FormValidate) => {
    try {
      if (!realTransfer) return
      const normalizedItems = formData.items.map(item => ({
        product_id: item.product_id,
        response_type: item.response_type,
        approved_quantity: item.response_type === 'REJECTED' ? 0 : item.approved_quantity,
        response_reason: item.response_type !== 'FULL' ? item.response_reason?.trim() : undefined
      }))

      const allRejected = normalizedItems.every((item) => item.response_type === 'REJECTED')

      if (allRejected) {
        const reason = normalizedItems
          .map((item) => item.response_reason)
          .filter(Boolean)
          .join(' | ') || 'Solicitud rechazada desde POS.'

        await transferService.rejectTransferRequest(realTransfer.id, { reason })
        toast.success('Solicitud rechazada exitosamente')
        onConfirm(selectedTransfer, {})
        onOpenChange(false)
        return
      }

      const payload = {
        items: normalizedItems
      }

      await transferService.shipTransferRequest(realTransfer.id, payload)
      toast.success('Envío confirmado exitosamente')

      onConfirm(selectedTransfer, {})
      onOpenChange(false)
    } catch (error: unknown) {
      const responseError = error as { response?: { status?: number; data?: { message?: string } } }
      // Si el backend rechaza por falta de stock
      if (responseError.response?.status === 400 || responseError.response?.status === 422) {
        toast.error(responseError.response?.data?.message || 'Stock insuficiente o error de validación en el servidor')
      } else {
        toast.error('Ocurrió un error al confirmar el envío')
      }
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] flex-col overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Revisar Solicitud de Transferencia</DialogTitle>
          <DialogDescription className="sr-only">
            Revisar y confirmar qué productos se pueden enviar
          </DialogDescription>
        </DialogHeader>
        {realTransfer && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Detalles de la Solicitud */}
            <div className="rounded-lg bg-slate-50 p-4">
              <h4 className="mb-3 text-sm font-medium text-slate-900">
                Detalles de la Solicitud
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">ID Solicitud:</span>
                  <span className="font-medium text-slate-900">
                    TRF-{String(realTransfer.id).padStart(4, '0')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Fecha:</span>
                  <span className="font-medium text-slate-900">
                    {realTransfer.created_at?.split('T')[0]}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Desde:</span>
                  <span className="font-medium text-slate-900">
                    {realTransfer.origin_sucursal?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Hacia:</span>
                  <span className="font-medium text-slate-900">
                    {realTransfer.destination_sucursal?.name}
                  </span>
                </div>
              </div>
            </div>

            {/* Listado de Productos */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-900">
                Productos Solicitados
              </h4>
              <p className="text-xs text-slate-600">
                Indica qué productos enviarás. Si no tienes stock, marca "No enviar".
              </p>
              <div className="space-y-3">
                {fields.map((field, idx) => {
                  const currentItem = watchItems[idx]
                  if (!currentItem) return null

                  return (
                    <div
                      key={field.id}
                      className="space-y-3 rounded-lg border border-slate-200 p-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {currentItem.product_name}
                          </p>
                          <p className="text-xs text-slate-600">
                            {currentItem.barcode ? `CB: ${currentItem.barcode} | ` : ''}Solicitado: {currentItem.requested_quantity} unidades
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            currentItem.response_type === 'FULL'
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : currentItem.response_type === 'PARTIAL'
                                ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                                : 'border-red-200 bg-red-50 text-red-700'
                          }
                        >
                          {currentItem.response_type === 'FULL' && 'Envío completo'}
                          {currentItem.response_type === 'PARTIAL' && 'Envío parcial'}
                          {currentItem.response_type === 'REJECTED' && 'No se envía'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setValue(`items.${idx}.response_type`, 'FULL')
                            setValue(`items.${idx}.approved_quantity`, currentItem.requested_quantity)
                            setValue(`items.${idx}.response_reason`, '')
                          }}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${currentItem.response_type === 'FULL'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-slate-200 hover:border-slate-300'
                            }`}
                        >
                          Completo
                        </button>
                        <button
                          type="button"
                          disabled={currentItem.requested_quantity <= 1}
                          onClick={() => {
                            setValue(`items.${idx}.response_type`, 'PARTIAL')
                            setValue(`items.${idx}.approved_quantity`, currentItem.requested_quantity > 1 ? currentItem.requested_quantity - 1 : 1)
                            setValue(`items.${idx}.response_reason`, '')
                          }}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${currentItem.response_type === 'PARTIAL'
                              ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                              : 'border-slate-200 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50'
                          }`}
                        >
                          Parcial
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setValue(`items.${idx}.response_type`, 'REJECTED')
                            setValue(`items.${idx}.approved_quantity`, 0)
                          }}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${currentItem.response_type === 'REJECTED'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-slate-200 hover:border-slate-300'
                            }`}
                        >
                          No enviar
                        </button>
                      </div>

                      {currentItem.response_type === 'PARTIAL' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-700">
                            Cantidad a Enviar
                          </label>
                          <Input
                            type="number"
                            min={1}
                            max={Math.max(1, currentItem.requested_quantity - 1)}
                            {...register(`items.${idx}.approved_quantity`, { valueAsNumber: true })}
                            className={`h-8 ${errors.items?.[idx]?.approved_quantity ? 'border-red-500' : ''}`}
                          />
                          {errors.items?.[idx]?.approved_quantity && (
                            <FormError message={errors.items[idx]?.approved_quantity?.message} />
                          )}
                        </div>
                      )}

                      {(currentItem.response_type === 'REJECTED' ||
                        currentItem.response_type === 'PARTIAL') && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-700">
                            Motivo
                          </label>
                          <Textarea
                            {...register(`items.${idx}.response_reason`)}
                            placeholder="Indica por qué no se puede enviar completo..."
                            rows={2}
                            className={`text-xs ${errors.items?.[idx]?.response_reason ? 'border-red-500' : ''}`}
                          />
                          {errors.items?.[idx]?.response_reason && (
                            <FormError message={errors.items[idx]?.response_reason?.message} />
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Resumen */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Resumen del Envío
              </p>
              <div className="space-y-1 text-sm text-blue-800">
                <p>
                  Productos completos:{' '}
                  {watchItems?.filter((v) => v.response_type === 'FULL').length || 0}
                </p>
                <p>
                  Productos parciales:{' '}
                  {watchItems?.filter((v) => v.response_type === 'PARTIAL').length || 0}
                </p>
                {(watchItems?.filter((v) => v.response_type === 'REJECTED').length || 0) > 0 && (
                  <p className="text-red-700">
                    Productos no enviados:{' '}
                    {watchItems?.filter((v) => v.response_type === 'REJECTED').length || 0}
                  </p>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-3">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? <DiscreteLoader /> : 'Confirmar Envío'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
